import { PHONE_REGEX, BARCODE_REGEX, AUTH_CODE_REGEX } from './constants';

/**
 * Validate phone number
 */
export function validatePhone(phone: string): boolean {
  if (!phone) return false;
  return PHONE_REGEX.test(phone);
}

/**
 * Validate barcode
 */
export function validateBarcode(barcode: string): boolean {
  if (!barcode) return false;
  return BARCODE_REGEX.test(barcode);
}

/**
 * Validate authentication code
 */
export function validateAuthCode(code: string): boolean {
  if (!code) return false;
  return AUTH_CODE_REGEX.test(code);
}

/**
 * Validate email
 */
export function validateEmail(email: string): boolean {
  if (!email) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validate required field
 */
export function validateRequired(value: string | number | null | undefined): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
}

/**
 * Validate minimum length
 */
export function validateMinLength(value: string, minLength: number): boolean {
  if (!value) return false;
  return value.length >= minLength;
}

/**
 * Validate maximum length
 */
export function validateMaxLength(value: string, maxLength: number): boolean {
  if (!value) return true; // Allow empty for max length
  return value.length <= maxLength;
}

/**
 * Validate number range
 */
export function validateRange(value: number, min: number, max: number): boolean {
  return value >= min && value <= max;
}

/**
 * Validate positive number
 */
export function validatePositive(value: number): boolean {
  return value > 0;
}

/**
 * Validate non-negative number
 */
export function validateNonNegative(value: number): boolean {
  return value >= 0;
}

/**
 * Get validation error message
 */
export function getValidationError(
  field: string,
  type: 'required' | 'phone' | 'barcode' | 'authCode' | 'email' | 'minLength' | 'maxLength' | 'positive',
  ...args: any[]
): string {
  const messages: Record<string, (...args: any[]) => string> = {
    required: () => `${field} is required`,
    phone: () => `Invalid phone number format`,
    barcode: () => `Invalid barcode format (8-13 digits)`,
    authCode: () => `Invalid authentication code (6 digits)`,
    email: () => `Invalid email format`,
    minLength: (min: number) => `${field} must be at least ${min} characters`,
    maxLength: (max: number) => `${field} must be at most ${max} characters`,
    positive: () => `${field} must be greater than 0`,
  };

  const messageFn = messages[type];
  return messageFn ? messageFn(...args) : `Invalid ${field}`;
}

/**
 * Validate cart item
 */
export function validateCartItem(item: {
  compositeId: number;
  quantity: number;
  discount?: number;
}): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!item.compositeId || item.compositeId <= 0) {
    errors.push('Invalid product');
  }

  if (!item.quantity || item.quantity <= 0) {
    errors.push('Quantity must be greater than 0');
  }

  if (item.discount && item.discount < 0) {
    errors.push('Discount cannot be negative');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Validate payment amount
 */
export function validatePaymentAmount(
  paymentAmount: number,
  totalAmount: number
): { valid: boolean; error?: string } {
  if (paymentAmount < totalAmount) {
    return {
      valid: false,
      error: 'Payment amount is insufficient',
    };
  }

  return { valid: true };
}

/**
 * Sanitize string input
 */
export function sanitizeString(input: string): string {
  if (!input) return '';
  
  // Remove potentially dangerous characters
  return input
    .replace(/[<>]/g, '')
    .trim();
}

/**
 * Validate UUID format
 */
export function validateUUID(uuid: string): boolean {
  if (!uuid) return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
}
